import * as CookieConsent from 'vanilla-cookieconsent';

// Forms protected by reCAPTCHA: keep the form visible (Borlabs-style) but block
// the reCAPTCHA part with an in-place placeholder + unblock buttons, and disable
// the submit until consent. The reCAPTCHA field node is removed so the form
// plugin (Elementor/WPForms) does not try to initialise grecaptcha (which throws
// while the script is blocked). On accept we reload so the form re-initialises
// with reCAPTCHA available.

const SERVICE = 'recaptcha';
const CATEGORY = 'functional';
const GATED_ATTR = 'data-occ-form-gated';

const FORM_SELECTORS = ['.wpforms-container', 'form.elementor-form', 'form.wpforms-form'];
const RECAPTCHA_FIELD_SELECTORS = '[class*="recaptcha"], .g-recaptcha, [data-sitekey]';
const SUBMIT_SELECTORS = 'button[type="submit"], input[type="submit"], .wpforms-submit';

const TEXTS = {
  de: {
    notice: 'Sie müssen den Inhalt von reCAPTCHA laden, um das Formular abzuschicken. Bitte beachten Sie, dass dabei Daten mit Drittanbietern ausgetauscht werden.',
    unblock: 'Inhalt entsperren',
    acceptAll: 'Erforderlichen Service akzeptieren und Inhalte entsperren',
    more: 'Mehr Informationen',
  },
  en: {
    notice: 'You need to load reCAPTCHA content to submit the form. Please note that data will be exchanged with third parties.',
    unblock: 'Unblock content',
    acceptAll: 'Accept required service and unblock content',
    more: 'More information',
  },
};

function recaptchaPresent(): boolean {
  return !!document.querySelector(`script[data-occ-service="${SERVICE}"]`);
}

function button(label: string, primary: boolean, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.style.cssText =
    'cursor:pointer;padding:.6em 1em;border:none;border-radius:.35em;font-weight:600;font-size:.9em;' +
    (primary
      ? 'background:var(--cc-btn-primary-bg,#2271b1);color:#fff'
      : 'background:#2271b1;color:#fff');
  btn.addEventListener('click', onClick);
  return btn;
}

/** Block reCAPTCHA-protected forms in place while consent is missing. */
export function setupFormGuard(lang: 'de' | 'en', privacyUrl?: string): void {
  // Gate on the CATEGORY: the reCAPTCHA script is category-gated (see
  // class-content-blocker.php), so once `functional` is accepted it loads.
  if (!recaptchaPresent() || CookieConsent.acceptedCategory(CATEGORY)) {
    return;
  }
  const t = TEXTS[lang];

  document.querySelectorAll<HTMLElement>(FORM_SELECTORS.join(',')).forEach((form) => {
    if (form.getAttribute(GATED_ATTR) === '1') return;
    form.setAttribute(GATED_ATTR, '1');

    // Remove the reCAPTCHA field(s) so the form plugin does not init grecaptcha.
    form.querySelectorAll(RECAPTCHA_FIELD_SELECTORS).forEach((el) => el.remove());

    // Disable submit so the form can't be sent without reCAPTCHA.
    form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(SUBMIT_SELECTORS).forEach((s) => {
      s.disabled = true;
      s.style.opacity = '0.5';
      s.style.pointerEvents = 'none';
    });

    // Build the in-place placeholder.
    const ph = document.createElement('div');
    ph.style.cssText =
      'margin:1em 0;padding:1.1em 1.25em;border:1px solid #e2e8f0;border-radius:.5em;background:#f6f7f8;font-size:.92em;line-height:1.5';

    const p = document.createElement('p');
    p.innerHTML = t.notice.replace('reCAPTCHA', '<strong>reCAPTCHA</strong>');
    p.style.cssText = 'margin:0 0 1em';
    ph.appendChild(p);

    if (privacyUrl) {
      const a = document.createElement('a');
      a.href = privacyUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = t.more;
      a.style.cssText = 'display:inline-block;margin-bottom:1em;text-decoration:underline';
      ph.appendChild(a);
      ph.appendChild(document.createElement('br'));
    }

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:.6em';
    // Both unblock the reCAPTCHA (functional) category, then reload so the form
    // re-initialises with reCAPTCHA available.
    actions.appendChild(
      button(t.unblock, false, () => {
        CookieConsent.acceptCategory(CATEGORY);
        window.location.reload();
      })
    );
    actions.appendChild(
      button(t.acceptAll, true, () => {
        CookieConsent.acceptCategory(CATEGORY);
        window.location.reload();
      })
    );
    ph.appendChild(actions);

    // Place the notice where the reCAPTCHA / submit is.
    const submit = form.querySelector(SUBMIT_SELECTORS);
    if (submit && submit.parentNode) {
      submit.parentNode.insertBefore(ph, submit);
    } else {
      form.appendChild(ph);
    }
  });
}

/**
 * If reCAPTCHA was enabled elsewhere (e.g. the banner) while forms are still
 * gated, reload once so they re-initialise with reCAPTCHA available.
 */
export function formGuardOnConsentChange(): void {
  if (document.querySelector(`[${GATED_ATTR}="1"]`) && CookieConsent.acceptedCategory(CATEGORY)) {
    window.location.reload();
  }
}
